import type { ComponentProps, ReactNode } from 'react';

import { Button } from '@simplywallst/ui-core/next';

import type { NarrativeForm } from '../../utils/schema';
import type { ValidationErrors } from '../../utils/validation';
import { useNarrativeBack } from '../../hooks/useNarrativeBack';
import type { NarrativeAuthor as DeprecatedNarrativeAuthor } from '@/components/DeprecatedNarrativeAuthor';
import type { NarrativeAuthor } from '@/components/NarrativeAuthor/NarrativeAuthor';
import { NarrativeAuthor as FlaggedNarrativeAuthor } from '@/components/NarrativeAuthor';
import { NarrativeTitle } from './components/NarrativeTitle';
import { useFlag } from '@simplywallst/unleash';
import { Textfield } from '@simplywallst/ui-core';

type Props = {
  author: ComponentProps<typeof DeprecatedNarrativeAuthor>['author'];
  owner: ComponentProps<typeof NarrativeAuthor>['owner'];
  isLoading: boolean;
  values: NarrativeForm;
  errors: ValidationErrors<NarrativeForm>;
  onSubmit: () => void;
  onChange: (values: Partial<NarrativeForm>) => void;
  submitLabel: ReactNode;
  companyId: string;
};

export const NarrativeHeader = ({
  author,
  owner,
  isLoading,
  values,
  errors,
  onSubmit,
  onChange,
  submitLabel,
  companyId,
}: Props) => {
  const { goBack } = useNarrativeBack();

  const { enabled, ready } = useFlag('mono-narratives-ai-title');

  const hasAITitlesEnabled = enabled && ready;

  return (
    <div
      className="grid gap-x3 bg-p-grey-100 p-x3 lg:rounded-x2"
      data-testid="narrative-header"
    >
      {hasAITitlesEnabled ? (
        <NarrativeTitle
          values={values}
          onChange={onChange}
          error={errors.title}
          companyId={companyId}
        />
      ) : (
        <Textfield
          className="!text-lg !font-strong !text-solid sm:!text-mega"
          autoComplete="off"
          hiddenLabel
          label="Narrative title"
          name="narrative-title"
          type="text"
          styleType="standardReverse"
          placeholder="What's your take on this stock"
          value={values.title}
          onChange={(e) => onChange({ title: e.currentTarget.value })}
          error={errors.title}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
      )}

      <div className="grid grid-cols-[1fr_max-content]">
        <FlaggedNarrativeAuthor author={author} owner={owner} />
        <div className="grid grid-cols-[max-content_max-content] gap-x1">
          <Button isDisabled={isLoading} variant="secondary" onClick={goBack}>
            Cancel
          </Button>
          <Button
            isLoading={isLoading}
            isDisabled={isLoading}
            variant="primary"
            onClick={onSubmit}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
